import type { NpiSuggestionRun, NpiSuggestions } from '../model/types';
import type { NpiSuggestionsGateway } from '../ports/npiSuggestionsGateway';

import { getIdToken } from '@/services/auth';
import type { components } from '@primum/api-types';
import { ApiError, fetchClient } from '@/util/fetchClient';

type WireRun = components['schemas']['NpiSuggestionRunResponse']['data'];

type WireResult = Extract<WireRun, { status: 'done' }>['result'];

export function toNpiSuggestions(wire: WireResult): NpiSuggestions {
  return {
    candidates: wire.candidates.map((candidate) => ({
      npi: candidate.npi,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      middleName: candidate.middleName,
      credential: candidate.credential,
      specialty: candidate.specialty,
      city: candidate.city,
      state: candidate.state,
    })),
    confidence: wire.confidence,
    needsName: wire.needsName,
    bestNameGuess: wire.bestNameGuess,
    ipState: wire.ipState,
    nameSource: wire.nameSource,
    leadSource: wire.leadSource,
  };
}

export function toNpiSuggestionRun(wire: WireRun): NpiSuggestionRun {
  if (wire.status === 'running') {
    return { runId: wire.runId, status: 'running', stage: wire.stage };
  }

  return { runId: wire.runId, status: 'done', suggestions: toNpiSuggestions(wire.result) };
}

/**
 * The ID token, sent explicitly because fetchClient attaches none for a
 * pending Google signup, which is still to get its account.
 */
async function authHeaders(): Promise<{ Authorization?: string }> {
  const idToken = await getIdToken();

  return idToken ? { Authorization: `Bearer ${idToken}` } : {};
}

export const fetchNpiSuggestionsGateway: NpiSuggestionsGateway = {
  async startSuggestions(request) {
    const { data } = await fetchClient.POST('/v2/providers/suggestions', {
      headers: await authHeaders(),
      body: {
        name: request.name,
        state: request.state,
        referralCode: request.referralCode,
      },
    });

    if (!data?.data) {
      throw new Error('NPI suggestions response had no data');
    }

    return toNpiSuggestionRun(data.data);
  },
  async readRun(runId) {
    const read = await fetchClient
      .GET('/v2/providers/suggestions/{runId}', {
        headers: await authHeaders(),
        params: { path: { runId } },
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) {
          return null;
        }

        throw error;
      });

    if (read === null) {
      return null;
    }

    const { data } = read;

    if (!data?.data) {
      throw new Error('NPI suggestion run response had no data');
    }

    return toNpiSuggestionRun(data.data);
  },
};
